//================================================================
//  Tab: Supporting Documentation
//================================================================

//  Purpose: This tab displays all the Supporting Documentation associated to this application

//  Example:
//    <SupportingDocumentation></SupportingDocumentation>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetRoles, GetUser, SetToast } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import DownloadFileBlob from '../../../../../Components/DownloadFileBlob/DownloadFileBlob';
import AddSupportingDocumentationPane from './Components/AddSupportingDocumentationPane';

//Functions
import DocumentListener from '../../../../../Library/DocumentListener';
import ConvertDate from '../../../../../Library/ConvertDate';
import CheckInventoryRole from '../../../../../Library/CheckInventoryRole';
import DeleteFile from '../../../../../Library/DeleteFile';
import WriteDocument from '../../../../../Library/WriteDocument';

//Images
import IconSearch from '../../../../../Components/Images/Icon_Search_Grey.svg';
import IconUpload from '../../../../../Components/Images/Icon_Upload_White.svg';
import IconUploadDisabled from '../../../../../Components/Images/Icon_Upload_Grey.svg';
import DeleteIcon from '../../../../../Components/Images/Icon_Bin_Red.svg';
import DeleteIconDisabled from '../../../../../Components/Images/Icon_Bin_Grey.svg';

export default function SupportingDocumentation() {

  //------------------------------------------------------
  //  useContext & React Router
  //------------------------------------------------------

  const params = useParams();
  const getUser = useContext(GetUser);
  const getRoles = useContext(GetRoles);
  const setToast = useContext(SetToast);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('pending');
  const [roleStatus, setRoleStatus] = useState('read'); // read, download, create, write & admin

  // Holds the application document 
  const [application, setApplication] = useState();

  // Search Input
  const [searchInput, setSearchInput] = useState('');

  // Holds the filtered documents
  const [docsFiltered, setDocsFiltered] = useState([]);

  // Open or close the upload pane
  const [openUploadPane, setOpenUploadPane] = useState(false);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Handles search function
  function handleSearch(value) {

    setSearchInput(value);

    // Reset the Filter
    if (value.length === 0) {

      setDocsFiltered(application.supportingdocumentation);

    };

    // Search Filter
    //  1. Contract ID
    //  2. Short Description
    const searchResults = application?.supportingdocumentation?.filter((object) =>
      object?.filename?.toLowerCase().includes(value.toLowerCase()) ||
      object?.filedescription?.toLowerCase().includes(value.toLowerCase())
    );

    // Save the filtered search results to the useState
    setDocsFiltered(searchResults);

  };

  // Handles the deletion of files
  function handleDeleteDocument(filepathToDelete) {

    // Check role
    if (!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')) return;

    // Set pending toast
    setToast({
      'type': 'pending',
      'message': `Removing file`
    });

    if (!application || !application.supportingdocumentation) return;

    // Find document to be deleted
    const docToDelete = application.supportingdocumentation.find(doc =>
      doc.filepath === filepathToDelete
    );

    if (!docToDelete) return console.warn('Document not found for deletion.');

    // Extract file path
    const filePath = filepathToDelete.split(`${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/`)[1];
    if (!filePath) return;

    // Delete File from GCS
    return DeleteFile(filePath)
    .then(() => {

      const updatedDocs = application.supportingdocumentation.filter(doc => doc.filepath !== filepathToDelete);

      const updatedDocument = {
        supportingdocumentation: updatedDocs,
        lastmodifieddate: new Date(),
        lastmodifiedby: {
          email: getUser?.emailaddress,
          givenname: getUser?.givenname,
          surname: getUser?.surname,
        },
      };

      // Update Application Doc in Firestore
      return WriteDocument('applications', application.applicationid, updatedDocument, true)
        .then(() => {

          setApplication(prev => ({ ...prev, supportingdocumentation: updatedDocs }));
          setDocsFiltered(updatedDocs);

          // Success toast
          setToast({
            'type': 'success',
            'message': `Successfully removed ${docToDelete?.filename}`
          });

        })

    })
    .catch((error) => {

      console.log('Error deleting GCS file:', error);

      // Set error on toast
      setToast({
          'type': 'error',
          'message': 'Failed to remove file',
      });

    }) 
  }

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get Supporting Docs
  useEffect(() => {

    const applicationid = params.id;

    if (applicationid === '') return;
    if (applicationid === null) return;
    if (getRoles === undefined) return;
    if (getUser === undefined) return;

    // ============================================
    //  Perform role check
    // ============================================

    const roleStatus = CheckInventoryRole('applications', getRoles, getUser);
    setRoleStatus(roleStatus);

    // ============================================
    //  Fetch Data
    // ============================================

    function onLoadChange(document) {

      if (document === undefined) return  setPageStatus('forbidden');

      let results = [];

      // Handle missing 'supportingdocumentation' key
      if (document?.supportingdocumentation !== undefined) {

        results = document?.supportingdocumentation;

      }

      setApplication(document);
      setDocsFiltered(results);

      setPageStatus('onload');

    }

    function onError(error) {

      console.log(error);
      setPageStatus('error-fatal');

    }

    const unsubscribe = DocumentListener('applications', applicationid, onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser,getRoles]);


  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='px-[20px]'>
          <div className='Table-Container'>

            {/* ======== Total, Upload & Search ========== */}
            <div className='flex flex-row justify-between py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

              <label className='font-semibold self-center'>
                Total: {docsFiltered?.length} of {application?.supportingdocumentation ? application.supportingdocumentation.length : docsFiltered?.length} {/* Note: There is no supportingdocumentation field in FS until a doc is uploaded. */}
              </label>

              {/* ======== Upload & Search  ========== */}
              <div className='flex flex-row gap-3'>

              {/* Upload File */}
              <button disabled={!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')} className='Primary-Button flex flex-row gap-2 items-center' onClick={() => setOpenUploadPane(true)}>
                <img src={(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin') ? IconUpload : IconUploadDisabled} alt='upload-icon'></img>
                Upload File
              </button>

              <AddSupportingDocumentationPane
                application={application}
                paneOpen={openUploadPane}
                setPaneOpen={setOpenUploadPane}
              ></AddSupportingDocumentationPane>

              {/* Search Bar */}
              <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[300px]'>

                <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                <label htmlFor='searchInput'>
                  <input
                    id='searchInput'
                    className='border-none h-[40px] p-0 m-0 outline-none'
                    type='text'
                    placeholder='Search'
                    onChange={(e) => handleSearch(e.target.value)}
                    value={searchInput}
                  ></input>
                </label>
              </div>

              </div>

            </div>

            {/* Files Table */}
            <table className='w-full max-h-96 overflow-y-scroll'>
              <thead>
                <tr className='border-b-[#D8D8D8] border-solid border-b-2'>
                  <th className='py-[15px] px-[20px] font-medium'>File Name</th>
                  <th className='py-[15px] px-[20px] font-medium'>Short Description</th>
                  <th className='py-[15px] px-[20px] font-medium'>Created By</th>
                  <th className='py-[15px] px-[20px] font-medium'>Created Date</th>
                  <th className='py-[15px] px-[20px] font-medium'></th>
                </tr>
              </thead>
              <tbody>
                {
                  // No SupportingDocumentation found
                  docsFiltered?.length === 0 ?
                    (
                      <tr>
                        <td colSpan={6}>
                          <div className='text-center p-[2%]'>
                            No documentation found.
                          </div>
                        </td>
                      </tr>
                    )
                    :
                    (
                      // Map in each contract
                      docsFiltered?.map((doc, index) => (

                        <tr key={index} className='border-b-[#C8C8C9] border-solid border-b align-top last:border-none'>

                          {/* File Name */}
                          <td className='py-[15px] px-[20px]'>

                            <DownloadFileBlob
                              filename={doc?.filename}
                              filepath={doc?.filepath}
                            ></DownloadFileBlob>

                          </td>

                          {/* Short Description */}
                          <td className='py-[15px] px-[20px]'>
                            <div className='leading-normal overflow-hidden text-ellipsis'>
                              {doc?.filedescription}
                            </div>
                          </td>

                          {/* Created By */}
                          <td className='py-[15px] px-[20px]'>
                            <b className='font-medium'>{doc?.createdbyname}</b>
                            <br></br>
                            {doc?.createdbyemail}
                          </td>

                          {/* Created Date */}
                          <td className='py-[15px] px-[20px] min-w-[200px]'>
                            {ConvertDate(doc?.createddate)}
                          </td>

                          {/* Remove Icon */}
                          <td className='py-[15px] px-[20px]'>
                            <img 
                              src={(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin') ? DeleteIcon : DeleteIconDisabled} 
                              alt='delete' 
                              width='35px' 
                              height='35px'
                              onClick={() => handleDeleteDocument(doc?.filepath)}
                            ></img>
                          </td>

                        </tr>
                      ))
                    )
                  }
              </tbody>

            </table>

          </div>
        </div>

      }
    ></PageComponent>
  )
}
